<template>
  <main-layout>
    <v-container>
      <v-layout justify-center align-center>
        <v-flex>
          <h1 class="headline">ข้อมูลสมาชิก (AIRA Member)</h1>
        </v-flex>
        <v-flex shrink>
          <v-btn color="primary" class="mr-2" @click="handleShowSearch"
            ><v-icon>search</v-icon>Search</v-btn
          >
          <v-btn @click="showExportMembers = true">Export</v-btn>
        </v-flex>
      </v-layout>

      <v-data-table
        :headers="headers"
        :pagination.sync="pagination"
        :rows-per-page-items="rowsPerPageItems"
        :items="members"
        class="elevation-1"
        :loading="loading"
        :total-items="total"
      >
        <template #items="props">
          <td>{{ props.item.aira_id || '-' }}</td>
          <td>
            {{ props.item.first_name }}{{ props.item.last_name ? ` ${props.item.last_name}` : '' }}
          </td>
          <td>{{ props.item.phoneno }}</td>
          <td>{{ props.item.email }}</td>
          <td>{{ props.item.app }}</td>
          <td>{{ props.item.login_timestamp | dateFormat('DD/MM/YYYY HH:mm') }}</td>
        </template>
      </v-data-table>
    </v-container>
    <v-dialog v-model="showSearch" max-width="800">
      <v-card>
        <v-card-text>
          <div class="text-xs-right">
            <v-btn icon small @click="showSearch = false"><v-icon>close</v-icon></v-btn>
          </div>

          <v-text-field
            v-model="filter.aira_id"
            class="default-input mb-2"
            outline
            name="aira_id"
            hide-details
            single-line
            clearable
            maxlength="255"
            :placeholder="$t('AIRA Customer ID')"
          />
          <v-text-field
            v-model="filter.social_name"
            class="default-input mb-2"
            outline
            name="social_name"
            hide-details
            single-line
            clearable
            maxlength="255"
            :placeholder="$t('Social Name')"
          />
          <v-text-field
            v-model="filter.phone_number"
            class="default-input mb-2"
            outline
            name="phone_number"
            hide-details
            single-line
            clearable
            maxlength="255"
            :placeholder="$t('Phone Number')"
          />
          <v-text-field
            v-model="filter.email"
            class="default-input mb-2"
            outline
            name="email"
            hide-details
            single-line
            clearable
            maxlength="255"
            :placeholder="$t('Email')"
          />
          <v-select
            v-model="filter.channel"
            class="default-input mb-2"
            outline
            name="channel"
            :items="channels"
            hide-details
            single-line
            :placeholder="$t('Channel')"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="handleClearSearch">Clear</v-btn>
          <v-btn color="primary" @click="handleSearch">Search</v-btn>
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ExportGroup
      v-model="showExportMembers"
      :total="total"
      filename="member-lists"
      @onExport="handleExportMember"
    ></ExportGroup>
  </main-layout>
</template>

<script>
import { mapGetters } from 'vuex'
import airaApi from '@/api/aira'
import { handleResponse, handleApiError } from '@/api/util'
import ExportGroup from '@/components/elements/ExportGroup.vue'
export default {
  name: 'MemberManagement',
  components: {
    ExportGroup
  },
  data() {
    return {
      pagination: {
        rowsPerPage: 30
      },
      rowsPerPageItems: [10, 20, 30, 40],
      filter: {
        aira_id: '',
        social_name: '',
        phone_number: '',
        email: '',
        status: '',
        channel: 'All',
        date_to: '',
        date_from: ''
      },
      channels: ['All', 'facebook', 'line'],

      showSearch: false,
      headers: [
        { text: 'รหัสสมาชิก(AIRA Customer ID)', value: 'aira_id' },
        { text: 'Social Name', value: 'first_name' },
        { text: 'เบอร์โทรศัพท์', value: 'phoneno' },
        { text: 'อีเมล', value: 'email' },
        { text: 'ช่องทาง', value: 'app' },
        { text: 'วันที่ผูกบัญชี', value: 'login_timestamp', sortable: false }
      ],
      members: [],
      loading: true,
      total: 0,
      showExportMembers: false
    }
  },
  computed: {
    ...mapGetters({
      selectedBot: 'selectedBot'
    })
  },
  watch: {
    pagination: {
      handler() {
        this.handleLoadMemberTable()
      },
      deep: true
    }
  },
  methods: {
    async handleClearSearch() {
      this.filter = {
        aira_id: '',
        social_name: '',
        phone_number: '',
        email: '',
        status: '',
        channel: 'All',
        date_to: '',
        date_from: ''
      }
      this.handleSearch()
    },
    handleSearch() {
      this.showSearch = false
      this.handleLoadMemberTable()
    },
    handleShowSearch() {
      this.showSearch = true
    },
    async handleLoadMemberTable() {
      const { sortBy, descending, page, rowsPerPage } = this.pagination
      this.loading = true
      const { items, total } = await this.handleGetMember({
        sortBy,
        descending,
        limit: rowsPerPage,
        offset: (page - 1) * rowsPerPage
      })
      this.members = items || []
      this.total = total
      this.loading = false
    },
    async handleExportMember(callback) {
      if (callback) {
        this.loading = true
        const { sortBy, descending } = this.pagination
        let { limit, offset } = callback
        let { items } = await this.handleGetMember({ limit, offset, sortBy, descending })

        callback.submitData(items)
        this.loading = false
      }
    },
    handleTransformSortField({ sortBy }) {
      switch (sortBy) {
        case 'app':
          return 'channel'
        case 'first_name':
          return 'social_name'
        case 'phoneno':
          return 'phone_number'
        default:
          return sortBy
      }
    },
    async handleGetMember({ limit, offset, sortBy, descending }) {
      try {
        const params = {
          page_id: this.selectedBot.pageid,
          ...this.filter,
          channel: this.filter.channel === 'All' ? '' : this.filter.channel,
          sort_by: this.handleTransformSortField({ sortBy }),
          sort_type: descending ? 'desc' : 'asc',
          limit,
          offset
        }
        const response = await airaApi.getMembers({ params })

        await handleResponse(response)
        if (response.data.data) {
          return { items: response.data.data, total: response.data.total }
        } else {
          return { items: [], total: 0 }
        }
      } catch (error) {
        handleApiError(error)
      }
    }
  }
}
</script>

<style lang="scss" scoped></style>



// WEBPACK FOOTER //
// src/components/view/template/Aira/MemberManagement/MemberManagement.vue